// Btc15Tab — the desktop BTC 15 tab: the paper probability desk, wide.
//
// Same reads as Diamond's Btc15Panel (GET /btc15/now every 5 s, /scoreboard
// and /history every 60 s, /agent/{key} and /window/{ticker} on demand),
// laid out in two columns with room for every checkpoint. Between polls the
// tab moves on Coinbase's public tape and re-prices every agent three times
// a second from the desk's own arithmetic (btc15/math.js). Nothing here is
// a forecast of Bitcoin: the Sept 13, 2026 study found the 15-minute market
// is a coin flip priced to the cent, and the desk's own first two days (37%
// won, −Ⓩ306) agreed. The tab's job is to show — honestly — what each
// strategy is doing and what it has earned, and to put win rate NEXT to
// P&L after fees every time it is shown.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "../lib/appCommon";
import useLiveBtc from "./btc15/useLiveBtc";
import usePulse from "./btc15/usePulse";
import { mergeLive, lastEventByAgent } from "./btc15/pulse";
import { buildLiveRows } from "./btc15/liveRows";
import { Btc15Styles, LiveBadge, AgentDot, SubTabs, AgentStrip, ActivityFeed, PositionList, ResultTakeover } from "./diamond/Btc15LiveParts";
import WindowChart from "./btc15/WindowChart";
import { liveProbabilities, markPrice, unrealised, kalshiFee } from "./btc15/math";
import GlossaryContent, { InfoDot } from "./btc15/GlossaryContent";
import Indicators from "./btc15/indicators";

const POLL_NOW_MS = 5000;
const POLL_SLOW_MS = 60000;
const CLOCK_MS = 250;              // the countdown; the price rides the tape itself

const pct = (p) => (p == null ? "—" : `${Math.round(p * 100)}%`);
const cents = (p) => (p == null ? "—" : `${Math.round(p * 100)}¢`);
// two decimals always: at one, a 99.9¢ contract plus its fee still printed
// "99.9¢", reading as though the fee were free on the very trade whose whole
// margin IS the fee
const cents2 = (p) => (p == null ? "—" : `${(p * 100).toFixed(2)}¢`);
const mult = (p) => (p > 0 && p < 1 ? `${(1 / p).toFixed(2)}x` : "—");
const usd = (v) => (v == null ? "—" : `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })}`);
const clock = (iso) => { try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontVariantNumeric: "tabular-nums" };
const CHART_COLORS = { text: "var(--text)", muted: "var(--muted)", faint: "var(--dim)", border: "var(--divider)", green: "var(--green)", red: "var(--red)", accent: "var(--blue)" };
// The shared live parts (the freshness badge, the agent dots, the sub-tabs, the
// full-screen result) take their colours as real values, because they add an
// alpha to them; the desktop's own tokens are CSS variables, so these are the
// same colours as hex.
const DESK_T = { green: "#4ade80", red: "#f87171", accent: "#60a5fa", gold: "#FFD60A", bgBase: "#0b0f14",
  text: "var(--text)", muted: "var(--muted)", faint: "var(--dim)", border: "var(--divider)", surface: "#11161d", surface2: "rgba(255,255,255,0.05)" };
const NO_EVENTS = [];
const STATUS_WORD = { measuring: "measuring", graduated: "graduated", stopped: "stopped" };

function Stat({ label, value, sub, color, testId }) {
  return (
    <div className="card" data-testid={testId} style={{ padding: "12px 14px" }}>
      <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".06em", fontWeight: 700 }}>{label}</div>
      <div style={{ ...mono, fontSize: 22, fontWeight: 800, color: color || "var(--text)", marginTop: 4 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

function Judgement({ g }) {
  if (!g) return null;
  const color = g.status === "graduated" ? "var(--green)" : g.status === "stopped" ? "var(--red)" : "var(--blue)";
  const n = Number(g.n || 0), after = Number(g.judgeAfter || 200);
  return (
    <div data-testid="btc15-judgement" style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 4 }}>
      <span style={{ color, fontWeight: 800 }}>{STATUS_WORD[g.status] || g.status}</span> {n}/{after}{g.returnOnRisk != null ? ` · ${g.returnOnRisk >= 0 ? "+" : ""}${g.returnOnRisk}% on risk` : ""}
      <div style={{ height: 3, background: "var(--divider)", borderRadius: 2, marginTop: 3, overflow: "hidden" }}><div style={{ width: `${Math.min(100, (n / after) * 100)}%`, height: "100%", background: color }} /></div>
    </div>
  );
}

function AgentDetail({ agentKey, live, wager, nameOf, money, bankFmt, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    setD(null);
    axios.get(`${API}/btc15/agent/${agentKey}`).then((r) => { if (alive) setD(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [agentKey]);
  const s = d?.strategy;
  const p = live?.calibrated?.[agentKey], raw = live?.raw?.[agentKey];
  return (
    <div className="card" data-testid="btc15-agent-detail" style={{ padding: "12px 14px", borderColor: "rgba(96,165,250,0.35)" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 14 }}>{d?.name || nameOf(agentKey)} <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 500 }}>{s ? `${s.label} · v${s.version}` : ""}</span></h3>
        <button type="button" onClick={onClose} style={{ marginLeft: "auto", background: "transparent", border: "1px solid var(--divider)", color: "var(--muted)", borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontSize: 11 }}>close</button>
      </div>
      {!d && <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>…</div>}
      {d && (
        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 14, marginTop: 8 }}>
          <div>
            {s?.retired && (
              <div data-testid="btc15-detail-retired" style={{ padding: 8, borderRadius: 8, marginBottom: 8,
                border: "1px solid color-mix(in srgb, var(--red) 40%, transparent)", background: "color-mix(in srgb, var(--red) 8%, transparent)" }}>
                <div style={{ fontSize: 10, fontWeight: 900, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--red)" }}>Retired — takes no new wagers</div>
                <div style={{ fontSize: 11.5, marginTop: 4, lineHeight: 1.5 }}>{s.retired}</div>
              </div>
            )}
            <div style={{ fontSize: 12.5, lineHeight: 1.5, opacity: s?.retired ? 0.55 : 1 }} data-testid="btc15-detail-how">{s?.how}</div>
            <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.5, opacity: s?.retired ? 0.55 : 1 }}><b style={{ color: "var(--text)" }}>Hypothesis.</b> {s?.hypothesis}</div>
            <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 6 }}>Raw method: {d.label} — {d.how}</div>
            <Judgement g={d.graduation} />
            {d.graduation?.sweptToZbucks > 0 && <div style={{ fontSize: 11, color: "var(--green)", marginTop: 4 }}>Swept to ZBucks so far: {money(d.graduation.sweptToZbucks)}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 10, fontSize: 11 }}>
              {[["Calibrated", pct(p), "trades on this"], ["Raw method", pct(raw), "before correction"], ["Market", pct(live?.marketP), "Kalshi mid"]].map(([l, v, sub]) => (
                <div key={l} style={{ border: "1px solid var(--divider)", borderRadius: 8, padding: "6px 8px" }}>
                  <div style={{ fontSize: 9.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700 }}>{l}</div>
                  <div style={{ ...mono, fontSize: 16, fontWeight: 800 }}>{v}</div>
                  <div style={{ fontSize: 10, color: "var(--dim)" }}>{sub}</div>
                </div>
              ))}
            </div>
            {wager && (
              <div style={{ fontSize: 12, marginTop: 10 }}>
                <b>{wager.status === "open" ? "In this window" : wager.status === "closed" ? "Sold this window" : "Settled this window"}</b>{" "}
                <span className="tag" style={{ color: wager.side === "yes" ? "var(--green)" : "var(--red)" }}>{wager.side === "yes" ? "UP" : "DOWN"}</span>{" "}
                <span style={{ ...mono, color: "var(--muted)" }}>@ {cents(wager.price)} · m{wager.placedMinute ?? wager.checkpoint}{wager.exitMinute != null ? ` → m${wager.exitMinute} ${(wager.closeReason || "").replace("_", " ")}` : ""}</span>{" "}
                <b style={{ ...mono, color: (wager.pnl ?? wager.mark?.pnl ?? 0) >= 0 ? "var(--green)" : "var(--red)" }}>{money(wager.pnl ?? wager.mark?.pnl ?? 0)}</b>
              </div>
            )}
          </div>
          <div>
            <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700 }}>Brier by minute · lower is better</div>
            <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse", marginTop: 4 }}>
              <thead><tr style={{ color: "var(--muted)" }}><th style={{ textAlign: "left", fontWeight: 600 }}></th>{["0", "5", "10", "13"].map((cp) => <th key={cp} style={{ textAlign: "right", fontWeight: 600 }}>m{cp}</th>)}</tr></thead>
              <tbody>
                {[["Calibrated", d.brier], ["Raw", d.brierRaw], ["Market", d.market]].map(([lab, row]) => (
                  <tr key={lab}><td style={{ padding: "2px 0" }}>{lab}</td>{["0", "5", "10", "13"].map((cp) => {
                    const v = row?.[cp], m = d.market?.[cp];
                    const beats = lab !== "Market" && v != null && m != null && v < m;
                    return <td key={cp} style={{ ...mono, textAlign: "right", color: beats ? "var(--green)" : lab === "Market" ? "var(--muted)" : "var(--text)" }}>{v == null ? "—" : v.toFixed(3)}</td>;
                  })}</tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 10.5, color: "var(--dim)", marginTop: 4 }}>Correction: {["0", "5", "10", "13"].map((cp) => `m${cp} ${d.calibration?.params?.[cp] ? "fitted" : "75% market"}`).join(" · ")}</div>
            {(d.curve || []).length > 0 && (
              <div data-testid="btc15-curve" style={{ marginTop: 10 }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700 }}>Said → came true</div>
                {d.curve.map((b) => (
                  <div key={b.said} style={{ display: "grid", gridTemplateColumns: "40px 1fr 70px", gap: 6, alignItems: "center", fontSize: 11, ...mono, padding: "1px 0" }}>
                    <span style={{ color: "var(--muted)" }}>{pct(b.said)}</span>
                    <div style={{ position: "relative", height: 7, background: "var(--divider)", borderRadius: 4 }}>
                      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${b.came * 100}%`, background: Math.abs(b.came - b.said) < 0.1 ? "var(--green)" : "var(--blue)", borderRadius: 4 }} />
                      <div style={{ position: "absolute", left: `${b.said * 100}%`, top: -2, bottom: -2, width: 2, background: "var(--text)", opacity: 0.6 }} />
                    </div>
                    <span style={{ textAlign: "right" }}>{pct(b.came)} <span style={{ color: "var(--dim)" }}>n{b.n}</span></span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, marginTop: 10 }}>Last wagers</div>
            {(d.wagers || []).slice(0, 8).map((w) => (
              <div key={w.lockId} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11.5, padding: "3px 0", borderTop: "1px solid var(--divider)" }}>
                <span style={{ ...mono, color: "var(--muted)" }}>{clock(w.placedAt)}</span>
                <span><span className="tag" style={{ color: w.side === "yes" ? "var(--green)" : "var(--red)" }}>{w.side === "yes" ? "UP" : "DOWN"}</span> <span style={{ ...mono, color: "var(--muted)" }}>@{cents(w.price)} m{w.placedMinute ?? w.checkpoint}{w.exitMinute != null ? `→m${w.exitMinute}` : ""}</span></span>
                <span style={{ ...mono, color: "var(--dim)", fontSize: 10.5 }}>{w.era === "strategy" ? (w.closeReason || "").replace("_", " ") || w.status : w.era}</span>
                <span style={{ ...mono, fontWeight: 800, color: (w.pnl ?? w.mark?.pnl ?? 0) >= 0 ? "var(--green)" : "var(--red)" }}>{w.status === "open" ? money(w.mark?.pnl ?? 0) + " open" : money(w.pnl ?? 0)}</span>
              </div>
            ))}
            <div style={{ fontSize: 11, color: "var(--dim)", marginTop: 8 }}>Bank {bankFmt(d.bank ?? 0)} of {bankFmt(d.bankStart ?? 500)} start.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function WindowDetail({ ticker, nameOf, onClose }) {
  const [w, setW] = useState(null);
  useEffect(() => {
    let alive = true;
    setW(null);
    axios.get(`${API}/btc15/window/${ticker}`).then((r) => { if (alive) setW(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [ticker]);
  return (
    <div className="card" data-testid="btc15-window-detail" style={{ padding: "10px 14px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 13 }}>{w ? `${clock(w.openTime)}–${clock(w.closeTime)}` : ticker}</h3>
        {w?.settlement && <span style={{ fontSize: 11, color: "var(--muted)" }}>{w.settlement.result === "yes" ? "UP" : "DOWN"} · gap {w.settlement.gap >= 0 ? "+" : ""}{Number(w.settlement.gap || 0).toFixed(0)} · target {usd(w.target)}</span>}
        <button type="button" onClick={onClose} style={{ marginLeft: "auto", background: "transparent", border: "1px solid var(--divider)", color: "var(--muted)", borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontSize: 11 }}>close</button>
      </div>
      {w && <WindowChart path={w.path} target={w.target} wagers={w.wagers} nameOf={nameOf} colors={CHART_COLORS} openTime={w.openTime} height={200} testId="btc15-window-chart" />}
      {w && (w.wagers || []).map((x) => (
        <div key={x.lockId} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderTop: "1px solid var(--divider)", fontSize: 12 }}>
          <span><b>{nameOf(x.agentKey)}</b> <span className="tag" style={{ color: x.side === "yes" ? "var(--green)" : "var(--red)" }}>{x.side === "yes" ? "UP" : "DOWN"}</span></span>
          <span style={{ ...mono, color: "var(--muted)" }}>@{cents(x.price)} m{x.placedMinute ?? x.checkpoint}{x.exitMinute != null ? ` → m${x.exitMinute} ${(x.closeReason || "").replace("_", " ")}` : x.status !== "open" ? " → bell" : ""}</span>
          <span style={{ ...mono, fontWeight: 800, color: (x.pnl ?? 0) >= 0 ? "var(--green)" : "var(--red)" }}>{x.pnl != null ? `${x.pnl >= 0 ? "+" : "−"}Ⓩ${Math.abs(x.pnl).toFixed(2)}` : "open"}</span>
        </div>
      ))}
      {w && !(w.wagers || []).length && <div style={{ fontSize: 12, color: "var(--muted)", padding: "6px 0" }}>Nobody wagered in this window.</div>}
    </div>
  );
}

// A result, the moment it lands — confetti on a win, a sad face on a loss.
// The user's ask, 2026-09-16: an information icon and an explanation of every
// number. Same copy as the phone tab — both render btc15/GlossaryContent, so
// the two can never describe the same figure differently.
const GLOSS_PALETTE = {
  text: "var(--text)", muted: "var(--muted)", faint: "var(--dim)", border: "var(--divider)",
  surface: "transparent", accent: "var(--blue)", green: "var(--green)", red: "var(--red)",
};

function GlossaryModal({ termId, ctx, onClose }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return (
    <div data-testid="btc15-glossary-modal" onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 70, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "6vh 16px",
    }}>
      <div className="card" onClick={(e) => e.stopPropagation()} style={{
        width: 560, maxWidth: "100%", maxHeight: "80vh", display: "flex", flexDirection: "column", padding: 0,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "14px 16px 8px", borderBottom: "1px solid var(--divider)" }}>
          <b style={{ fontSize: 15 }}>What everything means</b>
          <span style={{ fontSize: 11, color: "var(--muted)" }}>every number, and how a decision is made</span>
          <button type="button" data-testid="btc15-glossary-close" onClick={onClose} aria-label="Close"
            style={{ marginLeft: "auto", background: "transparent", border: "1px solid var(--divider)", color: "var(--text)",
              borderRadius: 6, padding: "2px 9px", cursor: "pointer", fontSize: 13, lineHeight: 1.4 }}>✕</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "10px 16px 18px" }}>
          <GlossaryContent palette={GLOSS_PALETTE} ctx={ctx} termId={termId} />
        </div>
      </div>
    </div>
  );
}

export default function Btc15Tab() {
  const [now, setNow] = useState(null);
  const [board, setBoard] = useState(null);
  const [history, setHistory] = useState(null);
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);
  const [agentOpen, setAgentOpen] = useState(null);
  const [windowOpen, setWindowOpen] = useState(null);
  const [currency, setCurrency] = useState(() => { try { return localStorage.getItem("zi_btc15_currency") || "zbucks"; } catch { return "zbucks"; } });
  const [sub, setSub] = useState(() => { try { const v = localStorage.getItem("zi_btc15_sub"); return ["live", "agents", "trades", "record", "learn"].includes(v) ? v : "live"; } catch { return "live"; } });
  const pickSub = (id) => { setSub(id); try { localStorage.setItem("zi_btc15_sub", id); } catch {} };
  const [popQueue, setPopQueue] = useState([]);
  const [popsOn, setPopsOn] = useState(() => { try { return localStorage.getItem("zi_btc15_pops") !== "off"; } catch { return true; } });
  const [glossary, setGlossary] = useState(null);    // { termId } while the explain modal is open
  const seenResolved = useRef(null);            // null until the first REAL payload seeds it
  useEffect(() => { try { localStorage.setItem("zi_btc15_currency", currency); } catch {} }, [currency]);
  const livePts = useRef({ ticker: null, pts: [] });
  const btcLive = useLiveBtc();
  // The one-second lane: the live Kalshi book and this window's fills, once a
  // second, instead of /now's 5 s poll of a block the desk rewrites every 20 s.
  const pulseState = usePulse(useCallback(() => axios.get(`${API}/btc15/pulse`).then((r) => r.data), []));
  const liveQ = mergeLive(now, pulseState, Date.now());
  const loadNowRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try { const r = await axios.get(`${API}/btc15/now`); if (alive) { setNow(r.data); setErr(null); } }
      catch { if (alive) setErr("desk unreachable"); }
    };
    loadNowRef.current = load;
    load();
    const iv = setInterval(load, POLL_NOW_MS);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  // A new window opens: the lane sees it within a second, /now within five.
  const pulseTicker = pulseState.data?.ticker;
  useEffect(() => {
    if (pulseTicker && now?.window?.ticker && pulseTicker !== now.window.ticker) loadNowRef.current?.();
  }, [pulseTicker, now?.window?.ticker, pulseState.receivedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [b, h] = await Promise.all([axios.get(`${API}/btc15/scoreboard`), axios.get(`${API}/btc15/history?limit=24`)]);
        if (alive) { setBoard(b.data); setHistory(h.data); }
      } catch {}
    };
    load();
    const iv = setInterval(load, POLL_SLOW_MS);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  useEffect(() => { const iv = setInterval(() => setTick((t) => t + 1), CLOCK_MS); return () => clearInterval(iv); }, []);

  // Seed from the first REAL payload: seeding on the initial null render made
  // every already-settled wager look new and popped a card on every open.
  useEffect(() => {
    if (!now) return;
    const resolved = liveQ.wagers.filter((w) => w.status && w.status !== "open" && w.lockId);
    if (seenResolved.current === null) { seenResolved.current = new Set(resolved.map((w) => w.lockId)); return; }
    const fresh = resolved.filter((w) => !seenResolved.current.has(w.lockId));
    if (!fresh.length) return;
    fresh.forEach((w) => seenResolved.current.add(w.lockId));
    if (!popsOn) return;
    const batches = [];
    for (const phase of ["won", "lost"]) {
      const group = fresh.filter((w) => (Number(w.pnl || 0) >= 0) === (phase === "won"));
      if (group.length) batches.push({ phase, wagers: group, pnl: group.reduce((s, w) => s + Number(w.pnl || 0), 0), key: group.map((w) => w.lockId).join("|") });
    }
    if (batches.length) setPopQueue((q) => [...q, ...batches]);
  }, [now, liveQ.wagers, popsOn]);   // eslint-disable-line react-hooks/exhaustive-deps

  const win = now?.window;
  const live = now?.live;
  const secondsLeft = useMemo(() => {
    if (!win?.closeTime) return null;
    return Math.max(0, Math.round((new Date(win.closeTime).getTime() - Date.now()) / 1000));
  }, [win, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const minute = secondsLeft == null ? null : Math.max(0, Math.min(15, 15 - secondsLeft / 60));
  const btc = btcLive.price ?? live?.btc;
  const delta = btc != null && win?.target != null ? btc - win.target : null;
  // memoised: a fresh [] every render would make nameOf's useCallback useless
  // and re-render the chart's markers on every one of the 3-per-second ticks.
  const roster = useMemo(() => now?.roster || [], [now]);
  const nameOf = useCallback((k) => roster.find((r) => r.agentKey === k)?.name || k, [roster]);
  const labelOf = (k) => roster.find((r) => r.agentKey === k)?.label || "";
  const howOf = (k) => roster.find((r) => r.agentKey === k)?.strategy?.how || roster.find((r) => r.agentKey === k)?.how || "";
  const strategyOf = (k) => roster.find((r) => r.agentKey === k)?.strategy || now?.strategies?.[k];
  const gradOf = (k) => roster.find((r) => r.agentKey === k)?.graduation;
  const marketP = liveQ.marketP;
  const known = win?.knownSoFar ?? live?.knownSoFar;
  const checkpoints = now?.checkpoints || [];
  const cps = board?.rules?.checkpoints || now?.rules?.checkpoints || [0, 5, 10, 13];
  const sym = currency === "usd" ? "$" : "Ⓩ";
  const money = (v) => `${Number(v) < 0 ? "−" : "+"}${sym}${Math.abs(Number(v) || 0).toFixed(2)}`;
  const bankFmt = (v) => `${sym}${Math.round(Number(v) || 0).toLocaleString("en-US")}`;

  if (win && btcLive.price != null && minute != null) {
    const store = livePts.current;
    if (store.ticker !== win.ticker) { store.ticker = win.ticker; store.pts = []; }
    const last = store.pts[store.pts.length - 1];
    if (!last || last.btc !== btcLive.price) { store.pts.push({ m: minute, btc: btcLive.price }); if (store.pts.length > 900) store.pts.shift(); }
  }

  const probs = useMemo(() => {
    const lp = live && win ? liveProbabilities({ price: btc, target: win.target, minute: minute ?? live.minute, sigma15: live.sigma15 || now?.study?.sigma15,
      sigmaRecent: live.sigmaRecent, marketP, serverRaw: live.agentsRaw, calibration: now?.calibration }) : null;
    return { calibrated: lp?.calibrated || live?.agents || {}, raw: lp?.raw || live?.agentsRaw || {}, marketP, fromTape: !!lp };
  }, [live, win, btc, minute, marketP, now]);
  const wagerOf = (k) => liveQ.wagers.find((w) => w.agentKey === k);
  const nowMs = Date.now();
  const feedEvents = pulseState.data?.events || NO_EVENTS;
  const { stripRows, positionRows, openCount } = buildLiveRows({
    probs, wagers: liveQ.wagers, yesBid: liveQ.yesBid, yesAsk: liveQ.yesAsk, marketP, agreeMargin: Number(now?.rules?.agreeMargin ?? 0.01),
    lastEvent: lastEventByAgent(feedEvents), pulseState, nowMs, nameOf, labelOf, money, cents, pct,
    C: { green: DESK_T.green, red: DESK_T.red, accent: DESK_T.accent, faint: DESK_T.faint, muted: DESK_T.muted } });
  const dotOf = Object.fromEntries(stripRows.map((r) => [r.key, r.dot]));
  const grid = `1.3fr repeat(${cps.length}, 0.55fr) 0.75fr 1fr`;

  return (
    <div data-testid="btc15-tab" style={{ display: "grid", gap: 12 }}>
      <Btc15Styles />
      {/* ── header: the name, how live it is, the toggles ── */}
        <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>BTC 15 · paper desk</h2>
          <LiveBadge T={DESK_T} pulseState={pulseState} nowMs={nowMs} fromPulse={liveQ.fromPulse} />
          <span style={{ ...mono, fontSize: 11, color: "var(--muted)", marginLeft: "auto" }}>{now?.health?.status ? `desk ${now.health.status}` : err || "…"}</span>
          <button type="button" data-testid="btc15-pops-toggle" title={popsOn ? "Result cards on" : "Result cards off"}
            onClick={() => setPopsOn((v) => { const n = !v; try { localStorage.setItem("zi_btc15_pops", n ? "on" : "off"); } catch {} return n; })}
            style={{ background: popsOn ? "rgba(96,165,250,0.14)" : "transparent", border: "1px solid var(--divider)", borderRadius: 6, padding: "2px 7px", cursor: "pointer", fontSize: 12, lineHeight: 1, opacity: popsOn ? 1 : 0.5 }}>🎉</button>
          <button type="button" data-testid="btc15-info-open" title="What everything means" aria-label="What everything means"
            onClick={() => setGlossary({ termId: null })}
            style={{ background: "transparent", border: "1px solid var(--divider)", color: "var(--text)", borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontSize: 12, lineHeight: 1.4, fontWeight: 800 }}>ⓘ</button>
          <button type="button" data-testid="btc15-currency-toggle" onClick={() => setCurrency((c) => (c === "usd" ? "zbucks" : "usd"))} title="Kalshi prices are dollars; Ⓩ1 here is $1 of paper" style={{ ...mono, background: "transparent", border: "1px solid var(--divider)", color: "var(--text)", borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontSize: 11, fontWeight: 800 }}>⇄ {currency === "usd" ? "$ paper" : "Ⓩ"}</button>
        </div>

        {!win && (
          <div className="card" style={{ padding: 16, color: "var(--muted)", fontSize: 13 }}>
            {err ? "The desk is not reachable right now." : "No window recorded yet — the desk posts its first probabilities within a minute of starting."}
          </div>
        )}

      <SubTabs T={DESK_T} bleed={0} background="transparent" value={sub} onChange={pickSub}
        tabs={[{ id: "live", label: "Live" }, { id: "agents", label: "Agents" }, { id: "trades", label: "Trades", badge: openCount },
               { id: "record", label: "Record" }, { id: "learn", label: "Learn" }]} />

      {/* ── LIVE: the window on the left, who is doing what on the right ── */}
      {sub === "live" && (
        <div data-testid="btc15-sub-live" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
          <div style={{ display: "grid", gap: 12 }}>
            {win && (
              <>
            <div style={{ fontSize: 11.5, color: "var(--muted)" }}>Window {clock(win.openTime)}–{clock(win.closeTime)} · <span style={mono}>{win.ticker}</span></div>
            <div className="grid4">
              <Stat testId="btc15-target" label="Target" value={usd(win.target)} sub="60 s BRTI average before open" />
              <Stat testId="btc15-btc" label={<span>BTC now{btcLive.connected ? <span data-testid="btc15-live-dot" style={{ display: "inline-block", width: 6, height: 6, borderRadius: 3, background: "var(--green)", marginLeft: 6, animation: "pulse 1.6s infinite" }} /> : null}</span>} value={usd(btc)} sub={delta == null ? "" : `${delta >= 0 ? "+" : ""}${delta.toFixed(2)} vs target · ${btcLive.price != null ? `Coinbase tape${btcLive.tps ? ` · ${btcLive.tps} prints/s` : ""}` : "desk tick"}`} color={delta == null ? undefined : delta >= 0 ? "var(--green)" : "var(--red)"} />
              <Stat testId="btc15-clock" label="Time left" value={secondsLeft == null ? "—" : mmss(secondsLeft)} sub={minute == null ? "" : `minute ${minute.toFixed(0)} of 15`} />
              <Stat testId="btc15-market" label={<span style={{ display: "inline-flex", alignItems: "center" }}>Market · Up
                <InfoDot palette={GLOSS_PALETTE} size={11} testId="btc15-info-mid" label="What is the mid?"
                  onClick={() => setGlossary({ termId: "mid" })} /></span>} value={pct(marketP)} sub={liveQ.yesBid != null ? `bid ${pct(liveQ.yesBid)} · ask ${pct(liveQ.yesAsk)}` : "no quote"} color="var(--blue)" />
            </div>

            {liveQ.yesAsk != null && (
              <div className="card" style={{ padding: "10px 14px" }} data-testid="btc15-cost">
                <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, marginBottom: 6 }}>
                  What a contract costs, and what {sym}{Number(now?.rules?.stake ?? 5).toFixed(0)} returns
                  <InfoDot palette={GLOSS_PALETTE} size={12} testId="btc15-info-fee" label="What is the fee?"
                    onClick={() => setGlossary({ termId: "fee" })} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {[["Up", liveQ.yesAsk, "var(--green)", "btc15-cost-up"],
                    ["Down", liveQ.yesBid == null ? null : 1 - liveQ.yesBid, "var(--red)", "btc15-cost-down"]].map(([label, price, color, tid]) => {
                    if (price == null) return <div key={label} />;
                    const fee = kalshiFee(price);
                    const allIn = price + fee;
                    const s = Number(now?.rules?.stake ?? 5);
                    const contracts = s / price;              // the desk's own arithmetic, fractional as Kalshi settles
                    const outlay = s + kalshiFee(price, contracts);
                    const net = contracts - outlay;           // a winner pays $1 a contract at the bell
                    return (
                      <div key={label} data-testid={tid} style={{ border: `1px solid ${color}44`, borderLeft: `3px solid ${color}`, borderRadius: 8, padding: "8px 11px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                          <span style={{ fontWeight: 800, color }}>{label} · {pct(price)}</span>
                          <span style={{ ...mono, fontSize: 12, color: "var(--muted)" }}>{mult(allIn)} · pays $1</span>
                        </div>
                        <div style={{ ...mono, fontSize: 12.5, marginTop: 4 }}>
                          {cents2(price)} <span style={{ color: "var(--dim)" }}>+ {cents2(fee)} fee</span> = <b>{cents2(allIn)}</b> <span style={{ color: "var(--muted)" }}>a contract</span>
                        </div>
                        <div style={{ ...mono, fontSize: 12.5, marginTop: 2 }}>
                          <span style={{ color: "var(--muted)" }}>{sym}{s.toFixed(0)} → </span>
                          <b style={{ color }}>{sym}{contracts.toFixed(2)}</b>
                          <span style={{ color: "var(--dim)" }}> · {net >= 0 ? "+" : "−"}{sym}{Math.abs(net).toFixed(2)} net if right · {contracts.toFixed(1)} contracts</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 10.5, color: "var(--dim)", marginTop: 6 }}>
                  The fee is charged again on the way out, so a round trip pays it twice.
                </div>
              </div>
            )}
            <div className="card" style={{ padding: "10px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "var(--muted)" }}>
                <span style={{ display: "flex", alignItems: "center" }}>
                  Already decided at this minute — the study's curve, not a forecast
                  <InfoDot palette={GLOSS_PALETTE} size={12} testId="btc15-info-known" label="What does this mean?"
                    onClick={() => setGlossary({ termId: "knownsofar" })} />
                </span>
                <span style={{ ...mono, color: "var(--text)", fontWeight: 700 }} data-testid="btc15-known">{known != null ? `${known}%` : "—"}</span>
              </div>
              <div style={{ height: 6, background: "var(--divider)", borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
                <div style={{ width: `${known || 0}%`, height: "100%", background: "var(--blue)", transition: "width 600ms ease" }} />
              </div>
            </div>

            <div className="card" style={{ padding: "8px 10px 2px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, padding: "0 4px 4px" }}>
                <span>This window</span><span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 500 }}>▲▼ entries · ● exits · dotted: market P(Up)</span>
              </div>
              <WindowChart path={now?.path} livePoints={livePts.current.ticker === win.ticker ? livePts.current.pts : []} target={win.target} wagers={liveQ.wagers} nameOf={nameOf} colors={CHART_COLORS} openTime={win.openTime} height={190} />
            </div>
              </>
            )}
          </div>
          <div style={{ display: "grid", gap: 12 }}>
            <div className="card" style={{ padding: "8px 14px" }}>
              <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, padding: "4px 0" }}>Agents · right now</div>
              <AgentStrip T={DESK_T} rows={stripRows} onOpen={(k) => { setAgentOpen(k); pickSub("agents"); }} />
            </div>
            <div className="card" style={{ padding: "8px 14px" }}>
              <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, padding: "4px 0" }}>Just happened</div>
              <ActivityFeed T={DESK_T} events={feedEvents} pulseState={pulseState} nowMs={nowMs} nameOf={nameOf} money={money} cents={cents} limit={6}
                emptyText={pulseState.ok ? "Nothing traded in the last half hour." : "Waiting for the live feed…"} />
            </div>
          </div>
        </div>
      )}

      {/* ── AGENTS: every agent's read at every checkpoint ── */}
      {sub === "agents" && (
        <div data-testid="btc15-sub-agents" style={{ display: "grid", gap: 12 }}>
          {win ? (
            <>
            <div className="card" style={{ padding: "6px 14px" }}>
              <div style={{ display: "grid", gridTemplateColumns: grid, gap: 8, padding: "8px 0", borderBottom: "1px solid var(--divider)", fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700 }}>
                <span>Agent · strategy</span>{cps.map((c) => <span key={c} style={{ textAlign: "right" }}>m{c}</span>)}<span style={{ textAlign: "right" }}>now{probs.fromTape ? " · live" : ""}</span><span style={{ textAlign: "right" }}>position</span>
              </div>
              <div data-testid="btc15-agents">
                {Object.entries(probs.calibrated).map(([k, p]) => {
                  const w = wagerOf(k);
                  const g = gradOf(k);
                  const s = strategyOf(k);
                  const raw = probs.raw?.[k];
                  const mark = w && w.status === "open" ? markPrice(w, liveQ.yesBid, liveQ.yesAsk) : null;
                  const upnl = w && w.status === "open" ? (mark != null ? unrealised(w, mark) : w.mark?.pnl) : null;
                  return (
                    <div key={k} data-testid="btc15-agent-row" title={howOf(k)} onClick={() => setAgentOpen(agentOpen === k ? null : k)} style={{ display: "grid", gridTemplateColumns: grid, gap: 8, padding: "8px 0", borderBottom: "1px solid var(--divider)", alignItems: "baseline", cursor: "pointer", background: agentOpen === k ? "rgba(96,165,250,0.06)" : "transparent" }}>
                      <span><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><AgentDot T={DESK_T} dot={dotOf[k]} testId={`btc15-dot-${k}`} /><b>{nameOf(k)}</b></span><span style={{ display: "block", fontSize: 10.5, color: "var(--muted)" }}>{s?.label || labelOf(k)}</span><Judgement g={g} /></span>
                      {cps.map((c) => {
                        const rec = checkpoints.find((x) => x.minute === c);
                        const v = rec?.agents?.[k];
                        return <span key={c} style={{ ...mono, textAlign: "right", fontSize: 12.5, color: v == null ? "var(--dim)" : "var(--text)" }}>{v == null ? "·" : pct(v)}</span>;
                      })}
                      <span style={{ ...mono, textAlign: "right", fontSize: 15, fontWeight: 800, color: p >= 0.5 ? "var(--green)" : "var(--red)" }}>
                        {pct(p)}<span style={{ display: "block", fontSize: 10, color: "var(--muted)", fontWeight: 500 }}>{raw != null ? `raw ${pct(raw)} · ` : ""}{marketP == null ? "" : `${p - marketP >= 0 ? "+" : ""}${Math.round((p - marketP) * 100)} vs mkt`}</span>
                      </span>
                      <span style={{ ...mono, textAlign: "right", fontSize: 11.5 }}>
                        {w ? (
                          <>
                            <span className="tag" style={{ color: w.side === "yes" ? "var(--green)" : "var(--red)" }}>{w.side === "yes" ? "UP" : "DOWN"}</span> @{cents(w.price)} m{w.placedMinute ?? w.checkpoint}
                            <span style={{ display: "block", fontWeight: 800, color: (w.status === "open" ? upnl : w.pnl) >= 0 ? "var(--green)" : "var(--red)" }}>{w.status === "open" ? `${money(upnl ?? 0)} open` : w.status === "closed" ? `sold ${money(w.pnl)}` : `${w.status} ${money(w.pnl)}`}</span>
                          </>
                        ) : <span style={{ color: g?.status === "stopped" ? "var(--red)" : "var(--dim)" }}>{g?.status === "stopped" ? "stopped" : s?.window ? `watching · m${s.window[0]}–${s.window[1]}` : "watching"}</span>}
                      </span>
                    </div>
                  );
                })}
                <div style={{ display: "grid", gridTemplateColumns: grid, gap: 8, padding: "8px 0", alignItems: "baseline", color: "var(--muted)" }}>
                  <span><b>Market</b><span style={{ display: "block", fontSize: 10.5 }}>Kalshi mid — the benchmark</span></span>
                  {cps.map((c) => { const rec = checkpoints.find((x) => x.minute === c); return <span key={c} style={{ ...mono, textAlign: "right", fontSize: 12.5 }}>{rec?.marketP == null ? "·" : pct(rec.marketP)}</span>; })}
                  <span style={{ ...mono, textAlign: "right", fontSize: 15, fontWeight: 800 }}>{pct(marketP)}</span><span />
                </div>
              </div>
            </div>

            {agentOpen && <AgentDetail agentKey={agentOpen} live={probs} wager={wagerOf(agentOpen)} nameOf={nameOf} money={money} bankFmt={bankFmt} onClose={() => setAgentOpen(null)} />}
            </>
          ) : <div className="card" style={{ padding: 16, color: "var(--muted)", fontSize: 13 }}>No window is open, so there is no live read to show.</div>}
        </div>
      )}

      {/* ── TRADES: what is open, and every buy and sell ── */}
      {sub === "trades" && (
        <div data-testid="btc15-sub-trades" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
          <div style={{ display: "grid", gap: 12 }}>
            <div className="card" style={{ padding: "8px 14px" }}>
              <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, padding: "4px 0" }}>Open positions · marked to the live book</div>
              <PositionList T={DESK_T} rows={positionRows} />
            </div>
            {(liveQ.wagers).length > 0 && (
              <div className="card" style={{ padding: "8px 14px" }}>
                <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, marginBottom: 4 }}>Wagers this window</div>
                {liveQ.wagers.map((w) => (
                  <div key={w.lockId || w._id} data-testid="btc15-wager" style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "6px 0", borderTop: "1px solid var(--divider)", fontSize: 12.5 }}>
                    <span><b>{nameOf(w.agentKey)}</b> <span className="tag" style={{ color: w.side === "yes" ? "var(--green)" : "var(--red)" }}>{w.side === "yes" ? "UP" : "DOWN"}</span></span>
                    <span style={{ ...mono, color: "var(--muted)" }}>{sym}{Number(w.stake || 5).toFixed(0)} @ {pct(w.price)} · m{w.placedMinute ?? w.checkpoint} · model {pct(w.modelP)} · edge {Math.round((w.edge || 0) * 100)}¢{w.exitMinute != null ? ` · out m${w.exitMinute} (${(w.closeReason || "").replace("_", " ")})` : ""}</span>
                    <span style={{ ...mono, fontWeight: 800, color: (w.pnl ?? w.mark?.pnl ?? 0) >= 0 ? "var(--green)" : "var(--red)" }}>{w.status === "open" ? money(w.mark?.pnl ?? 0) : money(w.pnl ?? 0)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="card" style={{ padding: "8px 14px" }}>
            <div style={{ fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, padding: "4px 0" }}>Every buy and sell · last 30 minutes</div>
            <ActivityFeed T={DESK_T} events={feedEvents} pulseState={pulseState} nowMs={nowMs} nameOf={nameOf} money={money} cents={cents} limit={40}
              emptyText={pulseState.ok ? "Nothing traded in the last half hour." : "Waiting for the live feed…"} />
          </div>
        </div>
      )}

      {/* ── RECORD: how it has actually done ── */}
      {sub === "record" && (
        <div data-testid="btc15-sub-record" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.05fr) minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
          <div style={{ display: "grid", gap: 12 }}>
        <div className="card" style={{ padding: "8px 14px" }} data-testid="btc15-scoreboard">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <h3 style={{ margin: "6px 0", fontSize: 14 }}>Scoreboard</h3>
            <span style={{ ...mono, fontSize: 11, color: "var(--muted)" }}>{board?.windows != null ? `${board.windows} settled windows` : ""}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1.3fr repeat(4, 0.55fr) 0.7fr 0.9fr", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--divider)", fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700 }}>
            <span>Strategy</span>{cps.map((c) => <span key={c} style={{ textAlign: "right" }}>Brier m{c}</span>)}<span style={{ textAlign: "right" }}>Win %</span><span style={{ textAlign: "right" }}>P&amp;L</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1.3fr repeat(4, 0.55fr) 0.7fr 0.9fr", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--divider)", alignItems: "baseline", color: "var(--muted)", fontSize: 12.5 }} data-testid="btc15-market-row">
            <span><b>Market</b><span style={{ display: "block", fontSize: 10.5 }}>the benchmark</span></span>
            {cps.map((c) => <span key={c} style={{ ...mono, textAlign: "right" }}>{board?.market?.brier?.[String(c)] == null ? "—" : board.market.brier[String(c)].toFixed(3)}</span>)}
            <span style={{ textAlign: "right" }}>—</span><span style={{ textAlign: "right" }}>—</span>
          </div>
          {(board?.agents || []).map((a) => (
            <div key={a.agentKey} data-testid="btc15-score-row" title={a.strategy?.how || a.how} onClick={() => setAgentOpen(a.agentKey)} style={{ display: "grid", gridTemplateColumns: "1.3fr repeat(4, 0.55fr) 0.7fr 0.9fr", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--divider)", alignItems: "baseline", fontSize: 12.5, cursor: "pointer" }}>
              <span><b>{a.name || a.agentKey}</b><span style={{ display: "block", fontSize: 10.5, color: "var(--muted)" }}>{a.strategy?.label || a.label}{a.graduation ? ` · ${STATUS_WORD[a.graduation.status] || ""} ${a.graduation.n}/${a.graduation.judgeAfter}` : ""}</span></span>
              {cps.map((c) => {
                const b = a.brier?.[String(c)], m = board?.market?.brier?.[String(c)];
                const beats = b != null && m != null && b < m;
                return <span key={c} style={{ ...mono, textAlign: "right", color: beats ? "var(--green)" : "var(--text)" }}>{b == null ? "—" : b.toFixed(3)}</span>;
              })}
              <span style={{ ...mono, textAlign: "right" }}>{a.winRate == null ? "—" : `${a.winRate}%`}<span style={{ display: "block", fontSize: 10, color: "var(--muted)" }}>{a.wagers} wager{a.wagers === 1 ? "" : "s"}{a.sold ? ` · ${a.sold} sold` : ""}</span></span>
              <span style={{ ...mono, textAlign: "right", color: a.pnl > 0 ? "var(--green)" : a.pnl < 0 ? "var(--red)" : "var(--muted)" }}>{money(a.pnl || 0)}<span style={{ display: "block", fontSize: 10, color: "var(--muted)", fontWeight: 500 }}>{a.returnOnRisk != null ? `${a.returnOnRisk >= 0 ? "+" : ""}${a.returnOnRisk}% on risk · ` : ""}{a.bank != null ? `bank ${bankFmt(a.bank)}` : ""}</span></span>
            </div>
          ))}
          {!(board?.agents || []).length && <div style={{ fontSize: 12, color: "var(--muted)", padding: "8px 0" }}>No settled windows yet.</div>}
          <div style={{ fontSize: 11, color: "var(--muted)", padding: "8px 0 4px", lineHeight: 1.45 }}>
            {board?.reading || "Brier is the squared error of a probability: lower is better. An agent has an edge only if its Brier sits below the market's over thousands of windows and its P&L after fees is positive. Win rate alone proves nothing."}
          </div>
          {(board?.agents || []).some((a) => a.tilt) && (
            <div data-testid="btc15-tilt-era" style={{ fontSize: 11, color: "var(--muted)", padding: "0 0 6px", lineHeight: 1.45 }}>
              {board?.eras?.tilt || "The tilt era, before the strategies:"} {(board.agents || []).filter((a) => a.tilt).map((a) => `${a.name || a.agentKey} ${a.tilt.wins}/${a.tilt.wagers} ${money(a.tilt.pnl)}`).join(" · ")}
            </div>
          )}
          {(board?.agents || []).some((a) => a.firstNight) && (
            <div data-testid="btc15-first-night" style={{ fontSize: 11, color: "var(--muted)", padding: "0 0 6px", lineHeight: 1.45 }}>
              {board?.firstNightNote} {(board.agents || []).filter((a) => a.firstNight).map((a) => `${a.name || a.agentKey} ${a.firstNight.wins}/${a.firstNight.wagers} ${money(a.firstNight.pnl)}`).join(" · ")}
            </div>
          )}
        </div>
          </div>
          <div style={{ display: "grid", gap: 12 }}>
        <div className="card" style={{ padding: "8px 14px" }} data-testid="btc15-history">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <h3 style={{ margin: "6px 0", fontSize: 14 }}>Recent windows</h3>
            <span style={{ fontSize: 10.5, color: "var(--muted)" }}>click a row for its chart</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "0.9fr 0.6fr 0.7fr 0.8fr 0.9fr", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--divider)", fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700 }}>
            <span>Open</span><span>Result</span><span style={{ textAlign: "right" }}>Gap $</span><span style={{ textAlign: "right" }}>Market</span><span style={{ textAlign: "right" }}>Best Brier</span>
          </div>
          {(history?.windows || []).map((w) => {
            const s = w.scores || {};
            const best = Object.entries(s).filter(([k]) => k !== "market").sort((a, b) => a[1] - b[1])[0];
            return (
              <div key={w.ticker} data-testid="btc15-history-row" onClick={() => setWindowOpen(windowOpen === w.ticker ? null : w.ticker)} style={{ display: "grid", gridTemplateColumns: "0.9fr 0.6fr 0.7fr 0.8fr 0.9fr", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--divider)", fontSize: 12, alignItems: "baseline", cursor: "pointer", background: windowOpen === w.ticker ? "rgba(96,165,250,0.06)" : "transparent" }}>
                <span style={{ ...mono, color: "var(--muted)" }}>{clock(w.openTime)}</span>
                <span style={{ fontWeight: 800, color: w.result === "yes" ? "var(--green)" : w.result === "no" ? "var(--red)" : "var(--muted)" }}>{w.result === "yes" ? "UP" : w.result === "no" ? "DOWN" : "—"}</span>
                <span style={{ ...mono, textAlign: "right" }}>{w.gap == null ? "" : `${w.gap >= 0 ? "+" : ""}${Number(w.gap).toFixed(0)}`}</span>
                <span style={{ ...mono, textAlign: "right", color: "var(--muted)" }}>{pct(w.marketP)} @m{w.atMinute ?? "—"}</span>
                <span style={{ ...mono, textAlign: "right" }}>{best ? `${nameOf(best[0])} ${Number(best[1]).toFixed(3)}` : "—"}</span>
              </div>
            );
          })}
          {!(history?.windows || []).length && <div style={{ fontSize: 12, color: "var(--muted)", padding: "8px 0" }}>Nothing settled yet.</div>}
        </div>
        {windowOpen && <WindowDetail ticker={windowOpen} nameOf={nameOf} onClose={() => setWindowOpen(null)} />}
          </div>
        </div>
      )}

      {/* ── LEARN: what the numbers mean ── */}
      {sub === "learn" && (
        <div data-testid="btc15-sub-learn" style={{ display: "grid", gap: 12 }}>
          <button type="button" data-testid="btc15-learn-open" onClick={() => setGlossary({ termId: null })}
            style={{ textAlign: "left", padding: "13px 14px", borderRadius: 10, border: "1px solid rgba(96,165,250,0.4)", background: "rgba(96,165,250,0.08)", color: "var(--text)", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
            What everything means
            <span style={{ display: "block", fontSize: 11.5, color: "var(--muted)", fontWeight: 500, marginTop: 2 }}>every number on this tab, and how a decision is made →</span>
          </button>
          {win && (
            <div className="card" style={{ padding: "10px 14px" }}>
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "flex", alignItems: "center", fontSize: 10.5, color: "var(--muted)", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700, marginBottom: 8 }}>
                  Indicators
                  <InfoDot palette={GLOSS_PALETTE} size={12} testId="btc15-info-indicators" label="What are these charts?"
                    onClick={() => setGlossary({ termId: "fee" })} />
                </div>
                <Indicators palette={GLOSS_PALETTE} yesAsk={liveQ.yesAsk} btc={btc} target={win.target}
                  minute={minute} sigma15={live?.sigma15 ?? now?.study?.sigma15} marketP={marketP}
                  knownSoFar={known} stake={now?.rules?.stake} currency={currency} />
              </div>
            </div>
          )}
        </div>
      )}

      <ResultTakeover pop={popQueue[0] || null} queued={Math.max(0, popQueue.length - 1)} onClose={() => setPopQueue((q) => q.slice(1))} T={DESK_T} nameOf={nameOf} money={money} cents={cents} />
        {glossary && (
          <GlossaryModal termId={glossary.termId} onClose={() => setGlossary(null)}
            ctx={{ yesBid: liveQ.yesBid, yesAsk: liveQ.yesAsk, marketP, btc, target: win?.target, minute,
                   sigma15: live?.sigma15 ?? now?.study?.sigma15, stake: now?.rules?.stake, tps: btcLive.tps,
                   agentKey: agentOpen, raw: probs?.raw }} />
        )}

        <div style={{ fontSize: 11, color: "var(--muted)" }}>Public data: Kalshi KXBTC15M and Coinbase. Paper only — nothing real is wagered here.{currency === "usd" ? " Kalshi prices are dollars; Ⓩ1 here is $1 of paper." : ""}</div>
    </div>
  );
}
